import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  IconButton,
  Modal,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import SettingIcon from "../assets/images/finchatGPT/SettingIcon.png";
import { useSelector, useDispatch } from "react-redux";
import { toggleDarkMode } from "../redux/slices/themeSlice";
import {
  RootState,
  SettingsModalInterface,
} from "../assets/interfaces/interfaces";
import { fetchDeleteAllSessionsSlice } from "../redux/slices/deleteAllSessionsSlice";
import { AppDispatch } from "../redux/store";
import ConfirmDeleteModal from "./Sidebar/ConfirmDeletemodal";
import { fetchDeleteUser, resetState } from "../redux/slices/deleteUserSlice";
import { resetValidUserState } from "../redux/slices/validateUserSlice";

const SettingsModal: React.FC<SettingsModalInterface> = ({
  open,
  setOpen,
  generalActive,
  setGeneralActive,
  handleProfileMenuClose,
  clearAllChatData,
  handleSignOut
}) => {
  const handleClose = () => {
    setOpen(false);
    setGeneralActive(true);
    handleProfileMenuClose();
  };
  const accountDeletedStatus = useSelector((state: any) => state?.deleteUser?.status as any);
  const isDarkMode = useSelector((state: any) => state?.theme?.darkMode as any);
  const dispatch: AppDispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(false)
  const { messages } = useSelector((state: RootState) => state.chat);
  // Initialize theme based on Redux state
  const [theme, setTheme] = useState(isDarkMode ? "dark" : "light");
  const handleThemeChange = (event: SelectChangeEvent) => {
    const newTheme = event.target.value as string;
    setTheme(newTheme);
    dispatch(toggleDarkMode());
    localStorage.setItem(
      "tradegpt-darkmode",
      newTheme === "dark" ? "true" : "false"
    );
    document.body.className = newTheme === "dark" ? "dark" : "light";
  };
  const handleGeneralSettingsClick = () => {
    setGeneralActive(true);
  };
  const user = useSelector((state: any) => state.auth.currentUser);
  const userId = user?.userId || "";
  // const handleDataControlsClick = () => {
  //   setGeneralActive(false);
  // };

  const handleDeleteUser = () => {
    dispatch(fetchDeleteUser())
    setConfirmOpen(false)
    setDeleteUser(false)
  }

  useEffect(() => {
    if (accountDeletedStatus === "succeeded") {
      handleSignOut()
    }
  }, [accountDeletedStatus])

  const handleDeleteAllChats = () => {
    dispatch(fetchDeleteAllSessionsSlice({ userId }));
    clearAllChatData();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="pr-[23px] pt-[7px] pl-[7px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#1C1C1E] shadow md:w-[680px] md:h-[392px] rounded-lg">
        <ConfirmDeleteModal
          title="all chats"
          open={confirmOpen}
          setOpen={setConfirmOpen}
          handleClose={() => {
            setDeleteUser(false)
            setConfirmOpen(false)
          }}
          deleteUser={deleteUser}
          handleChatDelete={deleteUser ? handleDeleteUser : handleDeleteAllChats}
        />
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-white font-['Inter'] text-lg font-semibold leading-6 tracking-tighter mt-5 ml-6 pb-5">
            Settings
          </h1>
          <IconButton onClick={handleClose}>
            <CloseIcon className="text-white" />
          </IconButton>
        </div>
        <div className="flex">
          <div className="inline-flex flex-col items-start gap-2 flex-shrink-0 h-[320px] p-[16px] pt-0">
            <Button
              onClick={handleGeneralSettingsClick}
              startIcon={
                <img
                  src={SettingIcon}
                  alt="Data Controls"
                  className="w-4 h-4"
                />
              }
              classes={{
                root: `${generalActive ? "!bg-[#444654]" : ""
                  } !rounded-[8px] !h-[32px] !justify-start !text-white !font-['Inter'] !text-sm !font-normal !leading-4 !tracking-tighter !normal-case !w-[140px]`,
              }}
            >
              General
            </Button>
            {/* Hided data controls for when content is ready */}
            {/* <Button
              onClick={handleDataControlsClick}
              startIcon={
                <img
                  src={SettingIcon}
                  alt="Data Controls"
                  className="w-4 h-4"
                />
              }
              classes={{
                root: `${
                  !generalActive ? "!bg-[#444654]" : ""
                } !rounded-[8px] !h-[32px] !justify-start !text-white !font-inter !text-sm !font-normal !leading-4 !tracking-tighter !normal-case !w-[140px]`,
              }}
            >
              Data Controls
            </Button> */}
          </div>
          {generalActive ? (
            <div className="flex-1 flex flex-col justify-start items-start ml-4">
              <div className="flex justify-between items-center w-full">
                <h2 className="theme-text font-['Inter']">Theme</h2>
                <FormControl>
                  <Select
                    value={theme}
                    onChange={handleThemeChange}
                    displayEmpty
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      color: "white",
                      ".MuiSelect-select": { color: "white" },
                      border: "none",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiSvgIcon-root": { color: "white" },
                    }}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="w-full border-b border-gray-700 my-2"></div>
              <div className="flex flex-wrap   justify-center md:justify-between items-center w-full mt-4">
                <h2 className="delete-chats mb-5 md:mb-0">Delete All Chats</h2>
                <Button
                  disabled={!messages.length}
                  onClick={() => setConfirmOpen(true)}
                  classes={{
                    root: "!bg-[#AA2E26] !normal-case !text-white !font-['Inter'] flex items-center justify-center px-3 py-1.5 ml-auto rounded cursor-pointer hover:bg-red-700",
                  }}
                >
                  Delete
                </Button>
              </div>
              {/* <div className="flex flex-wrap   justify-center md:justify-between items-center w-full mt-4">
                <h2 className="delete-chats mb-5 md:mb-0">Delete User</h2>
                <Button
                  onClick={() => {
                    setDeleteUser(true)
                    setConfirmOpen(true)
                  }}
                  classes={{
                    root: "!bg-[#AA2E26] !normal-case !text-white !font-['Inter'] flex items-center justify-center px-3 py-1.5 ml-auto rounded cursor-pointer hover:bg-red-700",
                  }}
                >
                  Delete
                </Button>
              </div> */}
            </div>
          ) : (
            <div className=""></div>
          )}
        </div>
      </div>
    </Modal>
  );
};
export default SettingsModal;
